import React, { useState, useMemo, useEffect } from "react";
import { metalThemes, DEFAULT_THEME } from "../metalThemes";
import { useContext } from "react";
import { HueContext } from "../HueContext";
import { useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler
} from "chart.js";

  ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler
);

export default function Periodic() {
  const navigate = useNavigate();
  const hue = useContext(HueContext);
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [duration, setDuration] = useState("1Y");
  const symbol = selected?.symbol || "Cu";
  const theme = metalThemes[symbol] || DEFAULT_THEME;
  // ===== Chart fetch state (we'll use closes[] as "equity series" for convenience)
  const [chartPayload, setChartPayload] = useState({
    timestamps: [],
    closes: [],
    symbol: "",
    account: "",
    timeframe: "",
    period: "",
  });
  const [loadingChart, setLoadingChart] = useState(false);
  const [chartError, setChartError] = useState(null);

  // Map your duration dropdown -> Alpaca portfolio history params
  function mapDurationToPortfolioParams(d) {
    switch ((d || "1Y").toUpperCase()) {
      case "1D":
        return { period: "1D", timeframe: "15Min" };
      case "1M":
        return { period: "1M", timeframe: "1D" };
      case "3M":
        return { period: "3M", timeframe: "1D" };
      case "6M":
        return { period: "6M", timeframe: "1D" };
      case "1Y":
        return { period: "1A", timeframe: "1D" }; // 1 year (A = annum)
      case "ALL":
        return { period: "all", timeframe: "1D" };
      default:
        return { period: "1A", timeframe: "1D" };
    }
  }

  /* --------------------------------------------------
     ELEMENT DATA (Starter Set – Expandable to 118)
  ---------------------------------------------------*/
  const elements = [
    // Row 1
    { symbol: "H", name: "Hydrogen", col: 1, row: 1, category: "nonmetal", clickable: false },
    { symbol: "He", name: "Helium", col: 18, row: 1, category: "noble", clickable: false },

    // Row 2
    { symbol: "Li", name: "Lithium", col: 1, row: 2, category: "alkali", image: "/lithiumcoinfloat.png", clickable: true },
    { symbol: "Be", name: "Beryllium", col: 2, row: 2, category: "alkaline", clickable: false },
    { symbol: "B", name: "Boron", col: 13, row: 2, category: "metalloid", clickable: false },
    { symbol: "C", name: "Carbon", col: 14, row: 2, category: "nonmetal", image: "/carboncoinfloat.png", clickable: true },
    { symbol: "N", name: "Nitrogen", col: 15, row: 2, category: "nonmetal", clickable: false },
    { symbol: "O", name: "Oxygen", col: 16, row: 2, category: "nonmetal", clickable: false },
    { symbol: "F", name: "Fluorine", col: 17, row: 2, category: "halogen", clickable: false },
    { symbol: "Ne", name: "Neon", col: 18, row: 2, category: "noble", image: "/necoinfloat.png", clickable: true },

    // Row 3
    { symbol: "Na", name: "Sodium", col: 1, row: 3, category: "alkali", clickable: false },
    { symbol: "Mg", name: "Magnesium", col: 2, row: 3, category: "alkaline", clickable: false },
    { symbol: "Al", name: "Aluminum", col: 13, row: 3, category: "post", clickable: false },
    { symbol: "Si", name: "Silicon", col: 14, row: 3, category: "metalloid", clickable: false },
    { symbol: "P", name: "Phosphorus", col: 15, row: 3, category: "nonmetal", clickable: false },
    { symbol: "S", name: "Sulfur", col: 16, row: 3, category: "nonmetal", image: "/sulfurcoinfloat.png", clickable: true },
    { symbol: "Cl", name: "Chlorine", col: 17, row: 3, category: "halogen", clickable: false },
    { symbol: "Ar", name: "Argon", col: 18, row: 3, category: "noble", clickable: false },

    // Row 4
    { symbol: "K", name: "Potassium", col: 1, row: 4, category: "alkali", clickable: false },
    { symbol: "Ca", name: "Calcium", col: 2, row: 4, category: "alkaline", clickable: false },
    { symbol: "Sc", name: "Scandium", col: 3, row: 4, category: "transition", clickable: false },
    { symbol: "Ti", name: "Titanium", col: 4, row: 4, category: "transition", clickable: false },
    { symbol: "V", name: "Vanadium", col: 5, row: 4, category: "transition", clickable: false },
    { symbol: "Cr", name: "Chromium", col: 6, row: 4, category: "transition", clickable: false },
    { symbol: "Mn", name: "Manganese", col: 7, row: 4, category: "transition", clickable: false },
    { symbol: "Fe", name: "Iron", col: 8, row: 4, category: "transition", clickable: false },
    { symbol: "Co", name: "Cobalt", col: 9, row: 4, category: "transition", clickable: false },
    { symbol: "Ni", name: "Nickel", col: 10, row: 4, category: "transition", clickable: false },
    {
      symbol: "Cu",
      name: "Copper",
      col: 11,
      row: 4,
      category: "transition",
      image: "/file_00000000097871fd8bc4f5d035f0407a.png",
      clickable: true,
    },
    { symbol: "Zn", name: "Zinc", col: 12, row: 4, category: "transition", image: "/zinccoinfloat.png", clickable: true },
    { symbol: "Ga", name: "Gallium", col: 13, row: 4, category: "post", clickable: false },
    { symbol: "Ge", name: "Germanium", col: 14, row: 4, category: "metalloid", clickable: false },
    { symbol: "As", name: "Arsenic", col: 15, row: 4, category: "metalloid", clickable: false },
    { symbol: "Se", name: "Selenium", col: 16, row: 4, category: "nonmetal", clickable: false },
    { symbol: "Br", name: "Bromine", col: 17, row: 4, category: "halogen", clickable: false },
    { symbol: "Kr", name: "Krypton", col: 18, row: 4, category: "noble", clickable: false },

    // Row 5
    { symbol: "Rb", name: "Rubidium", col: 1, row: 5, category: "alkali", clickable: false },
    { symbol: "Sr", name: "Strontium", col: 2, row: 5, category: "alkaline", clickable: false },
    { symbol: "Y", name: "Yttrium", col: 3, row: 5, category: "transition", clickable: false },
    { symbol: "Zr", name: "Zirconium", col: 4, row: 5, category: "transition", clickable: false },
    { symbol: "Nb", name: "Niobium", col: 5, row: 5, category: "transition", clickable: false },
    { symbol: "Mo", name: "Molybdenum", col: 6, row: 5, category: "transition", clickable: false },
    { symbol: "Tc", name: "Technetium", col: 7, row: 5, category: "transition", clickable: false },
    { symbol: "Ru", name: "Ruthenium", col: 8, row: 5, category: "transition", clickable: false },
    { symbol: "Rh", name: "Rhodium", col: 9, row: 5, category: "transition", clickable: false },
    { symbol: "Pd", name: "Palladium", col: 10, row: 5, category: "transition", clickable: false },
    { symbol: "Ag", name: "Silver", col: 11, row: 5, category: "transition", image: "/silvercoinfloat.png", clickable: true },
    { symbol: "Cd", name: "Cadmium", col: 12, row: 5, category: "transition", clickable: false },
    { symbol: "In", name: "Indium", col: 13, row: 5, category: "post", clickable: false },
    { symbol: "Sn", name: "Tin", col: 14, row: 5, category: "post", clickable: false },
    { symbol: "Sb", name: "Antimony", col: 15, row: 5, category: "metalloid", clickable: false },
    { symbol: "Te", name: "Tellurium", col: 16, row: 5, category: "metalloid", clickable: false },
    { symbol: "I", name: "Iodine", col: 17, row: 5, category: "halogen", clickable: false },
    { symbol: "Xe", name: "Xenon", col: 18, row: 5, category: "noble", image: "/xecoinfloat.png", clickable: true },

    // Row 6
    { symbol: "Cs", name: "Cesium", col: 1, row: 6, category: "alkali", clickable: false },
    { symbol: "Ba", name: "Barium", col: 2, row: 6, category: "alkaline", clickable: false },

    { symbol: "Hf", name: "Hafnium", col: 4, row: 6, category: "transition", clickable: false },
    { symbol: "Ta", name: "Tantalum", col: 5, row: 6, category: "transition", clickable: false },
    { symbol: "W", name: "Tungsten", col: 6, row: 6, category: "transition", clickable: false },
    { symbol: "Re", name: "Rhenium", col: 7, row: 6, category: "transition", clickable: false },
    { symbol: "Os", name: "Osmium", col: 8, row: 6, category: "transition", clickable: false },
    { symbol: "Ir", name: "Iridium", col: 9, row: 6, category: "transition", clickable: false },
    { symbol: "Pt", name: "Platinum", col: 10, row: 6, category: "transition", image: "/platinumcoinfloat.png", clickable: true },
    { symbol: "Au", name: "Gold", col: 11, row: 6, category: "transition", image: "/goldcoinfloat.png", clickable: true },
    { symbol: "Hg", name: "Mercury", col: 12, row: 6, category: "transition", clickable: false },
    { symbol: "Tl", name: "Thallium", col: 13, row: 6, category: "post", clickable: false },
    { symbol: "Pb", name: "Lead", col: 14, row: 6, category: "post", clickable: false },
    { symbol: "Bi", name: "Bismuth", col: 15, row: 6, category: "post", clickable: false },
    { symbol: "Po", name: "Polonium", col: 16, row: 6, category: "metalloid", clickable: false },
    { symbol: "At", name: "Astatine", col: 17, row: 6, category: "halogen", clickable: false },
    { symbol: "Rn", name: "Radon", col: 18, row: 6, category: "noble", clickable: false },
  ];

  /* --------------------------------------------------
     CUSTOM COINS (ROWS 8 & 9)
  ---------------------------------------------------*/
  const customCoins = useMemo(
    () => [
      // ===== ROW 8 =====
      { symbol: "AGR", name: "Agriculture", col: 1, row: 8, category: "custom", image: "/agriculturecoinfloat.png", clickable: true },
      { symbol: "RBR", name: "Rubber", col: 2, row: 8, category: "custom", clickable: false },
      { symbol: "BIO", name: "Bio Tech", col: 3, row: 8, category: "custom", clickable: false },
      {
        symbol: "AI",
        name: "AI Coin",
        col: 4,
        row: 8,
        category: "custom",
        image: "/file_00000000275c71fd822fdbe4e140c8ca.png",
        clickable: true,
      },
      { symbol: "EGE", name: "Electric Grid", col: 5, row: 8, category: "custom", image: "/electricgridcoinfloat.png", clickable: true },
      { symbol: "Travel", name: "Travel Coin", col: 6, row: 8, category: "custom", clickable: false },
      { symbol: "CRY", name: "Crypto Mining", col: 7, row: 8, category: "custom", clickable: false },
      { symbol: "TV", name: "Television Coin", col: 8, row: 8, category: "custom", image: "/tvandfilmcoinfloat.png", clickable: true },
      { symbol: "DIA", name: "Diamond", col: 9, row: 8, category: "custom", image: "/diamondcoinfloat.png", clickable: true },
      { symbol: "SPC", name: "Spectra Coin", col: 10, row: 8, category: "custom", clickable: false },
      { symbol: "OIL", name: "Oil-Gas", col: 11, row: 8, category: "custom", image: "/oilandgascoinfloat.png", clickable: true },
      { symbol: "REE", name: "Rare Earth", col: 12, row: 8, category: "custom", image: "/rareearthcoinfloat.png", clickable: true },
      { symbol: "H2O", name: "Water", col: 13, row: 8, category: "custom", image: "/h2ocoinfloat.png", clickable: true },
      { symbol: "SPCE", name: "Space", col: 14, row: 8, category: "custom", image: "/spacecoinfloat.png", clickable: true },
      { symbol: "HLTH", name: "Health", col: 15, row: 8, category: "custom", image: "/healthcarecoinfloat.png", clickable: true },

      // ===== ROW 9 =====
      { symbol: "RLE", name: "Real Estate", col: 1, row: 9, category: "custom", image: "/housingcoinfloat.png", clickable: true },
      { symbol: "AUTO", name: "Auto Mobile", col: 2, row: 9, category: "custom", image: "/automotivecoinfloat.png", clickable: true },
      { symbol: "DSM", name: "Deep Sea Mining", col: 3, row: 9, category: "custom", image: "/deepseaminingcoinfloat.png", clickable: true },
      { symbol: "TIM", name: "Timber Coin", col: 5, row: 9, category: "custom", image: "/timbercoinfloat.png", clickable: true },
      {
        symbol: "U",
        name: "Uranium Coin",
        col: 6,
        row: 9,
        category: "custom",
        image: "/file_0000000038a871f58af1190611e6b8b7.png",
        clickable: true,
      },
      { symbol: "TECH", name: "Tech Coin", col: 7, row: 9, category: "custom", clickable: false },
    ],
    []
  );

  const allTiles = [...elements, ...customCoins];

  /* --------------------------------------------------
     MAP COIN SYMBOL -> ACCOUNT ALIAS
     These must match the aliases your server expects in .env:
       ALPACA_ID_AI / ALPACA_SECRET_KEY_AI
       ALPACA_ID_URANIUM / ...
       ALPACA_ID_COPPER / ...
  ---------------------------------------------------*/
  const COIN_ACCOUNT = useMemo(
    () => ({
      AI: "AI",
      U: "URANIUM",
      Cu: "COPPER",
      // Add more if you make more "accounts" for other coins
      // Example:
      // Au: "GOLD",
      // Ag: "SILVER",
    }),
    []
  );

  /* --------------------------------------------------
     FETCH PORTFOLIO (ACCOUNT VALUE) WHEN (selected, duration) CHANGES
     Calls: /api/portfolio?account=AI&period=...&timeframe=...
  ---------------------------------------------------*/
  useEffect(() => {
    if (!selected) return;

    const account = COIN_ACCOUNT[selected.symbol];
    if (!account) {
      setChartPayload({ timestamps: [], closes: [], symbol: "", account: "", timeframe: "", period: "" });
      setChartError(`No account mapping set for ${selected.symbol}`);
      return;
    }

    const { period, timeframe } = mapDurationToPortfolioParams(duration);
    const controller = new AbortController();

    async function loadPortfolio() {
      try {
        setLoadingChart(true);
        setChartError(null);

        const url = `${process.env.REACT_APP_API_URL}/api/portfolio?account=${encodeURIComponent(account)}&period=${encodeURIComponent(period)}&timeframe=${encodeURIComponent(timeframe)}`;
        const token = localStorage.getItem("spectraToken") || "";

        const resp = await fetch(url, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`Portfolio HTTP ${resp.status}`);

        const json = await resp.json();

          setChartPayload({
               timestamps: (json.timestamps || []).filter(Boolean),
               closes: (json.equity || []).filter(v => v !== null),
               symbol: selected.symbol,
               account,
               timeframe,
               period,
     });

      } catch (e) {
        if (e.name !== "AbortError") setChartError(String(e.message || e));
      } finally {
        setLoadingChart(false);
      }
    }

                 loadPortfolio();
            return () => controller.abort();
                   }, [selected, duration, COIN_ACCOUNT]);

  /* --------------------------------------------------
              CHART.JS DATA
  ---------------------------------------------------*/
        const chartData = useMemo(() => {

  const raw = chartPayload.closes || [];

  const labels = (chartPayload.timestamps || []).map((t) => {
    const d = new Date(t);
    return duration === "1D"
      ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : d.toLocaleDateString();
  });

  const data = raw.filter(v => v !== null);

  return {
    labels,
    datasets: [
      {
        label: selected?.symbol || "coin",
        data,
        borderColor: theme.border,
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 0,
        fill: false
      }
    ]
  };

}, [chartPayload, duration, selected, theme]);

            const chartOptions = useMemo(
    () => ({
           responsive: true,
           plugins: {
           legend: { display: false },
           tooltip: { enabled: true },
      },
        scales: {
        x: { ticks: { color: "#aaa" }, grid: { color: "#222" } },
        y: {
           ticks: { color: "#aaa" },
           grid: { color: "#222" },
           beginAtZero: false,
suggestedMin: chartPayload.closes.length ? Math.min(...chartPayload.closes) * 0.98 : 0,
suggestedMax: chartPayload.closes.length ? Math.max(...chartPayload.closes) * 1.02 : 1
         }
      },
    }),
    [chartPayload]
  );
        

          console.log("chart data length", chartPayload.closes.length);
  /* --------------------------------------------------
     RENDER
  ---------------------------------------------------*/
     return (

    
    <div style={styles.page}>
      <style>
        {`
         @keyframes rainbowBorder {
         0% { filter: hue-rotate(0deg); }
         100% { filter: hue-rotate(360deg); }
        }
        @keyframes float {
       0%   { transform: translateY(0px); }
        50%  { transform: translateY(-8px); }
        100% { transform: translateY(0px); }
       }
       `}
      </style>

      {/* Header Row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        
          <h2
          style={{
           ...styles.title,
            color: `hsl(${hue}, 100%, 60%)`,
            transition: "color 0.1s linear",
            textShadow: `0 0 12px hsl(${hue}, 100%, 60%)`
       }}
         >
    Spectra Periodic Table
     </h2>


        <button
          onClick={() => navigate("/analytics")}
          aria-label="Back to Analytics"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            width: 110,
            height: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src="/spectra-logo.jpg"
            alt="Back to Analytics"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              transition: "transform 0.2s ease",
            }}
          />
        </button>
      </div>

      {/* TOP BOX */}
      <div style={styles.topBox}>
        {!selected ? (
          <>
            <h3 style={styles.heading}> </h3>

            <div style={styles.grid}>
              {allTiles.map((tile) => (
                <button
                  key={tile.symbol}
                  disabled={!tile.clickable}
                  onClick={() => tile.clickable && setSelected(tile)}
                  onMouseEnter={() => setHovered(tile.symbol)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    ...styles.tile,
                    ...categoryStyles[tile.category],
                    gridColumn: `${tile.col} / span 1`,
                    gridRow: `${tile.row} / span 1`,
                    cursor: tile.clickable ? "pointer" : "default",
                    opacity: tile.clickable ? 1 : 0.55,
                    transform:
                      hovered === tile.symbol && tile.clickable ? "scale(1.07)" : "scale(1)",
                    boxShadow:
                      hovered === tile.symbol && tile.clickable
                        ? `${categoryStyles[tile.category]?.boxShadow}, 0 0 14px ${
                            categoryStyles[tile.category]?.border?.split(" ")[2]
                          }`
                        : categoryStyles[tile.category]?.boxShadow,
                  }}
                >
                  <div style={styles.symbol}>{tile.symbol}</div>
                  <div style={styles.name}>{tile.name}</div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div style={styles.detailHeader}>
              <h3
           style={{
             ...styles.heading,
             color: theme.border,
             textShadow: `0 0 8px ${theme.border}`
         }}
      >
         {selected.name} ({selected.symbol})
    </h3>

              <button onClick={() => setSelected(null)} style={styles.backBtn}>
                Back to Table
              </button>
            </div>

            <div style={styles.detailArea}>
              {/* LEFT SIDE */}
              <div style={styles.leftColumn}>
                <div style={styles.coinPreview}>
                  {selected?.image && (
                    <div style={styles.coinImageWrapper}>
                      <img
                        src={selected.image}
                        alt={`${selected.name} Coin`}
                        style={{
                          width: "200px",
                          height: "200px",
                          objectFit: "contain",
                          animation: "float 3s ease-in-out infinite",
                        }}
                      />
                    </div>
                  )}

                  <div style={{ opacity: 0.8 }}>Coin / Element Preview</div>
                </div>

                <div style={styles.statRow}>
                  <div style={styles.statBox}>
                    <div style={styles.statLabel}>Current Value</div>
                    <div style={styles.statValue}>
                      {chartPayload.closes?.length
                        ? `$${Number(chartPayload.closes[chartPayload.closes.length - 1]).toFixed(2)}`
                        : "$---"}
                    </div>
                  </div>

                  <div style={styles.statBox}>
                    <div style={styles.statLabel}>Period</div>
                    <div style={styles.statValue}>{duration}</div>
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE */}
              <div
                style={{
                  background: "#111",
                  borderRadius: 12,
                  padding: 20,
                  height: 350,
                  position: "relative",
                }}
              >
                {loadingChart && (
                  <div style={{ color: "#aaa", marginBottom: 8 }}>Loading chart…</div>
                )}
                {chartError && (
                  <div style={{ color: "tomato", marginBottom: 8 }}>{chartError}</div>
                )}

                  
               
                <Line key={selected?.symbol} data={chartData} options={chartOptions} />

                {/* duration dropdown */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "11%",
                    right: 16,
                    background: "#111",
                    padding: 4,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    zIndex: 50,
                    pointerEvents: "auto",
                  }}
                >
                  <select
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    style={{
                      background: "#000",
                      color: "#fff",
                      border: "1px solid #fff",
                      borderRadius: 6,
                      padding: "3px 6px",
                      fontSize: 11,
                      cursor: "pointer",
                      width: "50px",
                      textAlign: "center",
                    }}
                  >
                    <option value="1D">1D</option>
                    <option value="1M">1M</option>
                    <option value="3M">3M</option>
                    <option value="6M">6M</option>
                    <option value="1Y">1Y</option>
                    <option value="ALL">All</option>
                  </select>
                </div>

                <button
                  onClick={() => {
                    if (!chartPayload.closes.length) return alert("No chart data to add yet.");
                    const currentUser = localStorage.getItem("spectraUser") || "guest";
                    const key = `dashboardCoinCharts_${currentUser}`;
                    const existing = JSON.parse(localStorage.getItem(key) || "[]");
                    const filtered = existing.filter(c => c.symbol !== selected.symbol);
                    filtered.push({
                      symbol: selected.symbol,
                      name: selected.name,
                      account: chartPayload.account,
                      timestamps: chartPayload.timestamps,
                      closes: chartPayload.closes,
                      duration,
                      image: selected.image || null,
                    });
                    localStorage.setItem(key, JSON.stringify(filtered));
                    window.dispatchEvent(new Event("coinChartAdded"));
                    alert(`${selected.name} chart added to Dashboard`);
                  }}
                  style={{
                    position: "absolute",
                    bottom: 12,
                    right: 16,
                    background: "#000",
                    color: "#fff",
                    border: "1px solid #fff",
                    padding: "6px 14px",
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: "pointer",
                    transition: "0.2s ease",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = "#111")}
                  onMouseOut={(e) => (e.currentTarget.style.background = "#000")}
                >
                  Add Chart to Dashboard
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* DESCRIPTION BOX */}
      <div style={styles.newsBox}>
        {selected?.symbol === "Cu" ? (
          <>
            <h3 style={styles.heading}>Copper (Cu)</h3>
            <p style={styles.newsItem}>
              Copper (Cu) is a reddish-orange transition metal with exceptional electrical conductivity
              and corrosion resistance. Used in power systems, electronics, electric vehicles, and
              telecommunications, copper remains essential to modern civilization. Found in major deposits
              across Chile, Peru, the United States, and Africa, copper is extracted through mining,
              smelting, and electrorefining to create ultra-pure industrial material. Its distinctive
              metallic red color and green oxidation patina make it one of the most recognizable metals
              on Earth.
            </p>
          </>
        ) : selected?.symbol === "U" ? (
          <>
            <h3 style={styles.heading}>Uranium (U)</h3>
            <p style={styles.newsItem}>
              Uranium (U) is a dense radioactive actinide metal best known for powering nuclear reactors
              and producing immense energy from small amounts of material. Found in minerals such as
              uraninite and carnotite, uranium is mined in regions including Kazakhstan, Canada, Australia,
              and Namibia before being refined into nuclear-grade fuel. Known for fluorescent green uranium
              minerals and exceptional energy density, uranium plays vital roles in clean energy production,
              medicine, aerospace technologies, and advanced scientific research. Though naturally occurring,
              high-grade uranium deposits are strategically valuable due to growing global demand and limited
              concentrated supply.
            </p>
          </>
        ) : selected?.symbol === "AI" ? (
          <>
            <h3 style={styles.heading}>Artificial Intelligence (AI)</h3>
            <p style={styles.newsItem}>
              Artificial Intelligence (AI) represents the computational simulation of learning, reasoning,
              pattern recognition, and decision-making processes that traditionally required human intelligence.
              Though not a naturally occurring element or mineral, AI has emerged as one of the most
              transformative technological forces in modern civilization — powering automation, scientific
              discovery, intelligent systems, and digital infrastructure.
            </p>
            <p style={styles.newsItem}>
              Unlike physical materials extracted from the Earth, AI is built from mathematical models,
              computational frameworks, and vast amounts of structured information. Its value comes from
              the ability to recognize patterns, interpret data, adapt to changing environments, and assist
              humans in solving increasingly complex problems.
            </p>
          </>
        ) : (
          <>
            <h3 style={styles.heading}>About This Page</h3>
            <p style={styles.newsItem}>
              The Periodic Table Chart allows you to explore current coin prices and market values by clicking
              on different elements in the chart. Each element represents a tradable coin in the Spectra
              portfolio — select one to view its live price chart and market data.
            </p>
            <p style={styles.newsItem}>
              Once you have found a coin you want to track, use the <strong>Add Chart to Dashboard</strong> button
              to pin that coin's chart directly to your personal Dashboard. From the Dashboard you can monitor
              performance and execute buy or sell trades in real time.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------
   STYLES
---------------------------------------------------*/

const styles = {
  page: {
    maxWidth: 1100,
    margin: "32px auto",
    padding: 24,
    borderRadius: 12,
    background: "#1c1c1c",
    color: "white",
  },
  title: {
    color: "#ffd700",
    marginBottom: 16,
  },
  heading: {
    margin: 0,
    color: "#ffffffff",
  },
  topBox: {
    background: "#2a2a2a",
    padding: 18,
    borderRadius: 12,
    marginBottom: 20,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(18, 52px)",
    gridAutoRows: "52px",
    gap: 6,
    justifyContent: "center",
    margin: "0 auto",
    padding: 15,
    borderRadius: 18,
    background: "rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.08)",
    overflowX: "auto",
  },
  tile: {
    borderRadius: 10,
    padding: 4,
    transition: "all 0.2s ease",
    border: "1px solid rgba(255,255,255,0.15)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    textAlign: "center",
    background: "transparent",
  },
  symbol: {
    fontWeight: "bold",
    fontSize: 14,
    color: "#f1f1f1"
  },
  name: {
    fontSize: 9,
    marginTop: 4,
    color: "#f1f1f1"
  },
  detailHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  backBtn: {
    padding: "8px 16px",
    borderRadius: 8,
    background: "#111",
    color: "#ffd700",
    fontWeight: 600,
    cursor: "pointer",
    border: "2px solid",
    borderImage: "linear-gradient(90deg, red, orange, yellow, green, cyan, blue, violet) 1",
    animation: "rainbowBorder 4s linear infinite",
    transition: "all 0.2s ease",
  },
  detailArea: {
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr",
    gap: 16,
  },
  coinPreview: {
    background: "rgba(0,0,0,0.3)",
    borderRadius: 12,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  coinImageWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px 0",
    background: "radial-gradient(circle, rgba(255,140,0,0.08) 0%, transparent 70%)",
  },
  statBox: {
    background: "rgba(0, 0, 0, 0.3)",
    padding: 12,
    borderRadius: 8,
    flex: 1,
  },
  statLabel: {
    fontSize: 11,
    opacity: 0.8,
  },
  statValue: {
    fontSize: 20,
    fontWeight: "bold",
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  statRow: {
    display: "flex",
    gap: 12,
  },
  newsBox: {
    background: "#2a2a2a",
    padding: 18,
    borderRadius: 12,
    maxHeight: 300,
    overflowY: "auto",
  },
  newsItem: {
    marginTop: 10,
  },
};

/* --------------------------------------------------
   CATEGORY COLORS
---------------------------------------------------*/

const categoryStyles = {
  alkali: {
    border: "1px solid #ff5c5c",
    boxShadow: "0 0 6px #ff5c5c",
  },
  alkaline: {
    border: "1px solid #ff9c4d",
    boxShadow: "0 0 6px #ff9c4d",
  },
  transition: {
    border: "1px solid gold",
    boxShadow: "0 0 6px gold",
  },
  metalloid: {
    border: "1px solid #4dd2ff",
    boxShadow: "0 0 6px #4dd2ff",
  },
  nonmetal: {
    border: "1px solid #8faaff",
    boxShadow: "0 0 6px #8faaff",
  },
  halogen: {
    border: "1px solid #ff7ad9",
    boxShadow: "0 0 6px #ff7ad9",
  },
  noble: {
    border: "1px solid #c38fff",
    boxShadow: "0 0 6px #c38fff",
  },
  custom: {
    background: "linear-gradient(145deg, #2e2e2e, #20d77fff)",
    border: "1px solid rgba(255,215,0,0.5)",
    boxShadow: "0 0 10px rgba(255,215,0,0.4)",
  },
};